/**
 * DateTimePicker Component
 * Selector de fecha y hora — extiende el DatePicker de shadcn con
 * un selector de hora/minuto en formato 12h (AM/PM) dentro del mismo popover.
 *
 * Issue #98: Cálculo automático de horas de ayuno en módulo de muestras.
 * Formato 12h con AM/PM — estándar en Honduras y Centroamérica.
 */

import * as React from "react";
import { format, setHours, setMinutes } from "date-fns";
import { es, enUS, ptBR, type Locale } from "date-fns/locale";
import { CalendarIcon, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

const locales: Record<string, Locale> = {
  es: es,
  en: enUS,
  pt: ptBR,
};

interface DateTimePickerProps {
  /** Valor en formato ISO 8601 */
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Fecha máxima seleccionable (default: ahora) */
  toDate?: Date;
  fromDate?: Date;
}

export function DateTimePicker({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  toDate = new Date(),
  fromDate,
}: DateTimePickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const langCode = i18n.language?.split("-")[0] || "es";
  const locale = locales[langCode] || es;

  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  const hours24 = dateValue?.getHours() ?? 0;
  const minutes = dateValue?.getMinutes() ?? 0;

  // Conversión 24h → 12h
  const isPM = hours24 >= 12;
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;

  // Al seleccionar fecha en el calendario, preservar la hora actual
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    const newDate = setMinutes(setHours(date, hours24), minutes);
    onChange(newDate.toISOString());
  };

  const handleHoursChange = (delta: number) => {
    if (!dateValue) return;
    // Ciclo 1-12 en el bloque AM o PM actual
    let newHours12 = hours12 + delta;
    if (newHours12 > 12) newHours12 = 1;
    if (newHours12 < 1) newHours12 = 12;
    // Convertir de vuelta a 24h manteniendo AM/PM
    const newHours24 = isPM
      ? newHours12 === 12
        ? 12
        : newHours12 + 12
      : newHours12 === 12
        ? 0
        : newHours12;
    const newDate = setHours(dateValue, newHours24);
    if (toDate && newDate > toDate) return;
    onChange(newDate.toISOString());
  };

  const handleMinutesChange = (delta: number) => {
    if (!dateValue) return;
    const newMinutes = Math.min(59, Math.max(0, minutes + delta));
    const newDate = setMinutes(dateValue, newMinutes);
    if (toDate && newDate > toDate) return;
    onChange(newDate.toISOString());
  };

  const toggleAmPm = () => {
    if (!dateValue) return;
    const newHours24 = isPM ? hours24 - 12 : hours24 + 12;
    const newDate = setHours(dateValue, newHours24);
    if (toDate && newDate > toDate) return;
    onChange(newDate.toISOString());
  };

  // Display en formato 12h con AM/PM
  const displayValue = dateValue ? format(dateValue, "dd/MM/yyyy hh:mm a", { locale }) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-52 justify-start text-left font-normal",
            !dateValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">
            {displayValue ?? (placeholder || t("common.selectDateTime"))}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {/* Calendario */}
        <Calendar
          key={i18n.language}
          mode="single"
          selected={dateValue}
          onSelect={handleDateSelect}
          disabled={(date) => {
            if (toDate && date > toDate) return true;
            if (fromDate && date < fromDate) return true;
            return false;
          }}
          defaultMonth={dateValue}
          captionLayout="dropdown"
          fromYear={1920}
          toYear={new Date().getFullYear()}
          locale={locale}
          initialFocus
        />

        <Separator />

        {/* Selector de hora 12h */}
        <div className="p-3">
          <div className="flex items-center gap-1 mb-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{t("common.time", "Hora")}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Horas (1-12) */}
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-8 text-xs"
                onClick={() => handleHoursChange(1)}
                disabled={!dateValue}
              >
                ▲
              </Button>
              <span className="w-8 text-center text-sm font-mono font-medium">
                {String(hours12).padStart(2, "0")}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-8 text-xs"
                onClick={() => handleHoursChange(-1)}
                disabled={!dateValue}
              >
                ▼
              </Button>
            </div>

            <span className="text-lg font-medium pb-0.5">:</span>

            {/* Minutos */}
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-8 text-xs"
                onClick={() => handleMinutesChange(5)}
                disabled={!dateValue}
              >
                ▲
              </Button>
              <span className="w-8 text-center text-sm font-mono font-medium">
                {String(minutes).padStart(2, "0")}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-8 text-xs"
                onClick={() => handleMinutesChange(-5)}
                disabled={!dateValue}
              >
                ▼
              </Button>
            </div>

            {/* Toggle AM/PM */}
            <Button
              variant="outline"
              size="sm"
              className="ml-1 w-12 font-medium"
              onClick={toggleAmPm}
              disabled={!dateValue}
            >
              {isPM ? "PM" : "AM"}
            </Button>

            {/* Confirmar */}
            <Button
              size="sm"
              className="ml-1 self-center"
              onClick={() => setOpen(false)}
              disabled={!dateValue}
            >
              {t("common.ok", "OK")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
